// src/components/landing/AnalysisShowcase.jsx
import React, { useRef, useState } from 'react';
import Placeholder from './Placeholder';
import { IconCompliance, IconClimate, IconCost, IconVastu } from './LandingIcons';

// Echoes the in-app analysis tabs, with the same subject-drawn icons. Screenshot
// slots stay placeholders until real captures are supplied — an illustration
// standing in for the product would be exactly the fabrication this page avoids.
//
// A real capture is picked up by file name, with no code change: put it in
// frontend/public/images/landing/ as compliance, climate, cost or vastu — .png,
// .jpg or .webp, cropped to 16:10 (1600 × 1000 is plenty). Until one exists, the
// slot keeps showing its placeholder.
const SHOT_DIR = `${process.env.PUBLIC_URL || ''}/images/landing`;
const SHOT_EXTS = ['.png', '.jpg', '.webp'];

/**
 * The tab's screenshot if one has been supplied, else its placeholder. Each
 * extension is tried in turn; the placeholder stays up until an image has actually
 * loaded, so a missing file never flashes a broken-image icon. (A missing file comes
 * back as a 404 HTML page; it fails to decode as an image and lands in onError, which
 * moves on to the next extension and finally leaves the placeholder in place.)
 */
function Shot({ tab }) {
  const [ext, setExt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const PanelIcon = tab.icon;
  const tried = ext >= SHOT_EXTS.length;
  return (
    <div className={`lp-shot${loaded ? ' lp-shot--image' : ''}`}>
      {!tried && (
        <img
          key={SHOT_EXTS[ext]}
          className="lp-shot-img"
          src={`${SHOT_DIR}/${tab.key}${SHOT_EXTS[ext]}`}
          alt={tab.alt}
          hidden={!loaded}
          onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(false); setExt((i) => i + 1); }}
        />
      )}
      {!loaded && (
        <>
          <span className="lp-shot-icon" aria-hidden="true"><PanelIcon /></span>
          <Placeholder>{tab.shot}</Placeholder>
        </>
      )}
    </div>
  );
}
const TABS = [
  { key: 'compliance', label: 'Compliance', icon: IconCompliance, shot: 'SCREENSHOT: a compliance verdict',
    alt: 'VAUOM showing a compliance verdict for a site, requirement by requirement',
    lines: [
      "The governing regulation is resolved from the plot's coordinates, road width and zone, then every requirement — setbacks, FSI, coverage, height — is computed for this site, one parameter at a time.",
      'Answers about a rule quote it and cite the rulebook and page.',
    ] },
  { key: 'climate', label: 'Climate', icon: IconClimate, shot: 'SCREENSHOT: a climate heatmap',
    alt: 'VAUOM showing a climate analysis heatmap over a massing',
    lines: [
      "Sun path, wind and thermal screening run over the massing you actually built, on this site's own climate data.",
      'Screening-grade, and labelled that way — so orientation and shading are decided while they are still cheap to change.',
    ] },
  { key: 'cost', label: 'Cost', icon: IconCost, status: 'Early estimate', shot: 'SCREENSHOT: a cost estimate',
    alt: 'VAUOM showing a cost estimate for a massing: a low–high range and where the cost goes',
    lines: [
      'Priced off the massing as soon as there is a form: a low–high range with a most-likely figure, where the cost goes, and a bill of quantities to download.',
      'Rates come from recent Gujarat project quotes and invoices, and the interior allowance is a placeholder — a feasibility range, not a quote.',
    ] },
  { key: 'vastu', label: 'Vastu', icon: IconVastu, shot: 'SCREENSHOT: the Vastu reading',
    alt: 'VAUOM showing a Vastu reading laid on the plot',
    lines: [
      "Laid on true north from the plot's own boundary, not estimated by eye.",
      'Every claim is quoted from a named text and cited to its printed page — including what that text does not say.',
    ] },
];

export default function AnalysisShowcase() {
  const [active, setActive] = useState('compliance');
  const tabRefs = useRef({});

  // WAI-ARIA tabs with automatic activation: arrows, Home and End move and select.
  const onKeyDown = (e) => {
    const i = TABS.findIndex((t) => t.key === active);
    let n = null;
    if (e.key === 'ArrowRight') n = (i + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') n = (i - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = TABS.length - 1;
    if (n == null) return;
    e.preventDefault();
    setActive(TABS[n].key);
    const el = tabRefs.current[TABS[n].key];
    if (el) el.focus();
  };

  const tab = TABS.find((t) => t.key === active);

  return (
    <section id="analyses" className="lp-section" aria-labelledby="lp-analyses-title">
      <div className="lp-wrap">
        <h2 id="lp-analyses-title" className="lp-h2">The analyses</h2>

        <div role="tablist" aria-label="Analyses" className="lp-tabs" onKeyDown={onKeyDown}>
          {TABS.map((t) => {
            const TabIcon = t.icon;
            const on = t.key === active;
            return (
              <button
                key={t.key}
                ref={(el) => { tabRefs.current[t.key] = el; }}
                type="button" role="tab" id={`lp-tab-${t.key}`}
                aria-selected={on} aria-controls={`lp-panel-${t.key}`}
                tabIndex={on ? 0 : -1}
                className="btn lp-tab"
                onClick={() => setActive(t.key)}
              >
                <span className="lp-tab-icon" aria-hidden="true"><TabIcon /></span>
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Keyed by tab, so a change remounts the panel and its fade-in replays. */}
        <div
          key={tab.key} role="tabpanel" id={`lp-panel-${tab.key}`}
          aria-labelledby={`lp-tab-${tab.key}`} tabIndex={0} className="lp-panel"
        >
          <Shot tab={tab} />
          <div className="lp-panel-text">
            <h3 className="lp-h3">
              {tab.label}
              {tab.status && <span className="lp-tag">{tab.status}</span>}
            </h3>
            {tab.lines.map((l) => <p key={l} className="lp-body">{l}</p>)}
          </div>
        </div>
      </div>
    </section>
  );
}
